import type { CityConfig } from "../city-template/types";

export const dublinConfig: CityConfig = {
  city: "Dublin",
  state: "Ohio",
  stateAbbr: "OH",
  slug: "dublin-oh",
  route: "/cities/dublin-oh",
  title: "Emergency Passport Dublin, OH | Expedited Passport Services | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Dublin, OH — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Franklin County and the Columbus suburbs. Get your passport fast!",
  h1: "Emergency & Expedited Passport Services in Dublin, OH",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport in Dublin? We help Columbus suburb and Franklin County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Dublin Ohio scenic bridge of historic Dublin over the Scioto River, charming suburban downtown with brick buildings and green landscaping, sunny afternoon with clear blue sky, professional Columbus Ohio suburb photography with vibrant colors and warm natural light",
  heroImageAlt: "Dublin Ohio scenic bridge over the Scioto River in charming suburban downtown",
  geoPosition: "40.0992;-83.1141",
  geoPlacename: "Dublin, Ohio",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "230 S Dearborn St, Suite 380, Chicago, IL 60604",
    distance: "Located in downtown Chicago — approximately 350 miles from Dublin via I-70",
  },
  nearbyCitiesZips: [
    { name: "Dublin", zips: "43016, 43017" },
    { name: "Powell", zips: "43065" },
    { name: "Hilliard", zips: "43026" },
    { name: "Worthington", zips: "43085, 43086" },
    { name: "Columbus", zips: "43201–43235" },
    { name: "Westerville", zips: "43081, 43082, 43086" },
    { name: "Upper Arlington", zips: "43220, 43221" },
    { name: "Delaware", zips: "43015" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Dublin departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-global-line", title: "Columbus Suburb Coverage", desc: "Serving Dublin, Powell, Hilliard, and the Franklin County suburbs" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Columbus, OH", route: "/cities/columbus-oh" },
    { name: "Fast passport renewal in Cincinnati, OH", route: "/cities/cincinnati" },
    { name: "Emergency passport help in Dayton, OH", route: "/cities/dayton" },
    { name: "Urgent passport services in Cleveland, OH", route: "/cities/cleveland" },
    { name: "Same-day passport help in Springfield, OH", route: "/cities/springfield-oh" },
    { name: "Passport courier services in Newark, OH", route: "/cities/newark-oh" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Dublin remotely via secure courier. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, achieving 24–48 hour turnaround for Franklin County travelers.",
  fpcOfficeDistance: "Serving all of Franklin County and the Columbus metropolitan area",
  serviceAreaIntro: "From Dublin's historic district to Powell, Hilliard, Worthington, and Upper Arlington, Fast Passport Center serves the entire Columbus suburb region.",
  nearbyCitiesIntro: "We serve Dublin neighborhoods and the surrounding Franklin County communities including Powell, Hilliard, Worthington, Columbus, Westerville, Upper Arlington, and Delaware.",
  howItWorksIntro: "Most Columbus suburb travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait in line at the regional agency.",
  mistakesIntro: "Dublin travelers often face a long drive to the Chicago Regional Passport Agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Franklin County and Columbus suburb travelers trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Dublin? Here are the most common ones we hear from Franklin County travelers.",
  statePageRoute: "/states/ohio",
  statePageName: "Ohio",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "5+ hour drive to Chicago agency",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail",
        "Can't upgrade speed after submission",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Dublin international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens." },
    { name: "New Passport", description: "First-time passport application assistance." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports." },
    { name: "Child Passport", description: "Passport services for minors under 16." },
    { name: "Name Change", description: "Passport correction and name change service." },
  ],
  testimonials: [
    {
      name: "David C.",
      location: "Dublin, OH",
      rating: 5,
      text: "My passport was 3 months expired and I had a business trip in 4 days. Fast Passport Center got it renewed in 3 days. Their agent answered every question I had.",
      service: "Passport Renewal",
    },
    {
      name: "Erin W.",
      location: "Powell, OH",
      rating: 5,
      text: "First-time passport for our family trip to the Caribbean. Fast Passport Center guided us to the right acceptance facility and had our passports ready in 5 days.",
      service: "New Passport",
    },
    {
      name: "Mike S.",
      location: "Hilliard, OH",
      rating: 5,
      text: "Emergency passport after a sudden death in the family. Fast Passport Center was compassionate, fast, and had my passport in 36 hours.",
      service: "Emergency Passport",
    },
    {
      name: "Jessica T.",
      location: "Worthington, OH",
      rating: 5,
      text: "My daughter's child passport was confusing — the two-parent consent rules were tricky. Fast Passport Center made it easy. Got it in 5 days.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "dublin-1",
      name: "Dublin Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "6565 Post Rd",
      city: "Dublin",
      state: "OH",
      zip: "43017",
      phone: "(614) 889-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Dublin neighborhood",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient Dublin facility. Call ahead for passport appointments.",
      icon: "ri-mail-line",
    },
    {
      id: "dublin-2",
      name: "Powell Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "49 W Olentangy St",
      city: "Powell",
      state: "OH",
      zip: "43065",
      phone: "(614) 761-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "North Dublin suburb",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Powell and Delaware County residents. Ample parking.",
      icon: "ri-mail-line",
    },
    {
      id: "dublin-3",
      name: "Hilliard Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "4101 Main St",
      city: "Hilliard",
      state: "OH",
      zip: "43026",
      phone: "(614) 876-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "West Columbus suburb",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Alternative for Hilliard and West Columbus residents.",
      icon: "ri-mail-line",
    },
    {
      id: "dublin-4",
      name: "Franklin County Clerk of Courts",
      type: "Court Clerk Passport Acceptance",
      address: "373 S High St",
      city: "Columbus",
      state: "OH",
      zip: "43215",
      phone: "(614) 525-3601",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM",
      distance: "Downtown Columbus",
      website: "https://clerk.franklincountyohio.gov/",
      websiteLabel: "Franklin County Clerk",
      notes: "Accepts passport applications downtown. Extended hours.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 5+ hour drive to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Passport photos must be exactly 2×2 inches with a white or off-white background. Many local photo shops use wrong specs.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Franklin County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many applicants show up with incorrect payment and have to reschedule.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Standard processing takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent for Minors",
      description: "For minors, both parents must appear or submit notarized consent. Don't miss this requirement.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop documents at any UPS location in Dublin." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Dublin address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Dublin before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Dublin for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround without the 5+ hour drive to Chicago." },
    { q: "How fast can I renew my passport in Dublin?", a: "We offer fast passport renewal in Dublin in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value." },
    { q: "What is the closest passport agency to Dublin, OH?", a: "The nearest regional passport agency to Dublin is the Chicago Passport Agency, approximately 350 miles away. For urgent processing without the long drive, Fast Passport Center's registered courier service achieves 24–48 hour turnaround." },
    { q: "Do I need to drive to Chicago for an emergency passport?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities." },
    { q: "Can I expedite a child passport in Dublin?", a: "Yes. We expedite child passports in Dublin for minors under 16. Both parents must appear or submit notarized consent." },
    { q: "What should I do if I lost my passport before a trip?", a: "Contact Fast Passport Center immediately. We provide lost passport replacement services in Dublin with DS-64 form guidance. Our emergency courier service can get your replacement in 24–48 hours." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Dublin", address: "5130 Frantz Rd, Dublin, OH 43017", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Dublin", address: "6250 Perimeter Loop Rd, Dublin, OH 43017", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart Supercenter — Dublin", address: "6670 Sawmill Rd, Dublin, OH 43017", hours: "Mon–Sun: 6AM–11PM" },
    { name: "FedEx Office — Dublin", address: "6590 Perimeter Loop Rd, Dublin, OH 43017", hours: "Mon–Fri: 8AM–8PM" },
  ],
  dmvLocations: [
    {
      name: "Dublin BMV Deputy Registrar",
      address: "6540 Sawmill Rd, Dublin, OH 43017",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Driver's license and REAL ID services. Appointment strongly recommended.",
      featured: true,
    },
    {
      name: "Hilliard BMV Deputy Registrar",
      address: "1634 Hilliard Rome Rd, Hilliard, OH 43026",
      hours: "Mon–Fri: 9AM–5PM",
      note: "Alternative for Hilliard and West Columbus residents. REAL ID available.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "CMH",
      name: "John Glenn Columbus International Airport",
      distance: "15 miles south of Dublin",
      address: "4600 International Gateway, Columbus, OH 43219",
      note: "Nearest airport for Dublin. Major hub with domestic and international flights.",
      flightUrl: "https://www.flycmh.com/",
      flightLabel: "Columbus Airport",
      featured: true,
    },
    {
      code: "DAY",
      name: "Dayton International Airport",
      distance: "75 miles west of Dublin",
      address: "3600 Terminal Dr, Dayton, OH 45377",
      note: "Alternative for domestic flights to major hubs.",
      flightUrl: "https://flydayton.com/",
      flightLabel: "Dayton Airport",
      featured: false,
    },
    {
      code: "CLE",
      name: "Cleveland Hopkins International Airport",
      distance: "130 miles north of Dublin",
      address: "5300 Riverside Dr, Cleveland, OH 44135",
      note: "Major hub with international routes. Consider for long-haul trips.",
      flightUrl: "https://www.clevelandairport.com/",
      flightLabel: "Cleveland Airport",
      featured: false,
    },
  ],
};